/**
 * Nova billing server functions — coach-side subscription status + portal +
 * checkout. Thin wrappers around `~/lib/server/nova-billing`.
 *
 * Aliases: nova billing functions, nova subscription api
 */

import { createServerFn } from "@tanstack/react-start";
import { getRequestUrl } from "@tanstack/react-start/server";
import { z } from "zod";

import type { CoachSelect } from "~/../drizzle/schema";
import { authMiddleware } from "~/lib/auth-middleware";
import {
  requireClerkCoachAuth,
  requireNovaCoachAuth,
} from "~/lib/coach-middleware";
import type { ProPlusLinkOutcome } from "~/lib/server/accelerator/pro-plus";
import type {
  NovaPlan,
  NovaPlanOption,
  NovaPrice,
  NovaSubscriptionState,
} from "~/lib/server/nova-billing";
import type {
  NovaPlanChangePreview,
  NovaPlanChangeResult,
} from "~/lib/server/nova-plan-change";
import type { NovaAccessState } from "~/lib/server/nova/access";
import {
  novaCancellationSchema,
  type NovaDowngradeOffer,
} from "~/lib/shared/nova/cancel-flow";
import {
  BILLING_TERMS,
  isSuitePlanLookupKey,
  planGrantsMarketingSuite,
  type SuitePlanLookupKey,
} from "~/lib/shared/plan-catalogue";
import { PLAN_CHANGE_MODES } from "~/lib/shared/plan-change";
import type { ReadinessResult } from "~/lib/shared/readiness/engine";
import { RETURN_PATH_MAX_LENGTH } from "~/lib/shared/return-path";
import { getAppUrl } from "~/lib/utils";

// Client-safe re-exports so route components never import from ~/lib/server.
export type {
  NovaAccessState,
  NovaPlan,
  NovaPlanChangePreview,
  NovaPlanChangeResult,
  NovaPlanOption,
  NovaPrice,
  ProPlusLinkOutcome,
};

/**
 * Subscription state for the Nova UI: the raw Stripe status plus the derived
 * access state, whether enforcement is on, trial eligibility, and the live
 * `default_nova` price so the footer/paywall/banner render from one query.
 */
export type NovaSubscriptionPayload = NovaSubscriptionState & {
  access: NovaAccessState;
  enforced: boolean;
  trialEligible: boolean;
  /** She holds a membership outside NOVA (`holdsDcmMembership`). It never
   *  grants NOVA; only the trial offer's words read it. */
  member: boolean;
  price: NovaPrice | null;
  /** Whether the coach has completed a 6-Figure Readiness Score. Drives the
   *  funnel hero's pre-score → scored transition. */
  hasScore: boolean;
  /** How many completed scores the coach has (the trajectory chip shows at
   *  >= 2). Zero whenever `hasScore` is false, which is the shape guard the
   *  funnel state machine checks. */
  scoreSnapshotCount: number;
  /** The coach's newest score, for display only — the funnel state machine
   *  gates on `hasScore`/`scoreSnapshotCount`, not this. */
  latestScore: ReadinessResult | null;
  /** Whether the coach has a published offer — the durable state behind the
   *  funnel's `sprint_live` milestone. */
  hasPublishedOffer: boolean;
};

const novaCheckoutInputSchema = z
  .object({
    /** The screen to come back to. The server checks it before any URL is
     *  built from it. */
    returnTo: z.string().max(RETURN_PATH_MAX_LENGTH).optional(),
    /** Which Nova price to buy. Absent means monthly, so a client that has
     *  never heard of terms keeps buying exactly what it bought before. */
    term: z.enum(BILLING_TERMS).optional(),
    /** The price the offer quoted, so a reprice between render and click is
     *  refused rather than charged silently. */
    quotedPriceId: z.string().max(255).optional(),
  })
  .optional();

/**
 * Builds the Nova subscription payload from a coach row already in hand.
 * Shared by `getNovaSubscription` (reads the coach as-is) and
 * `resyncNovaSubscription` (re-reads the coach after a Stripe sync) so both
 * server functions return the exact same shape from one code path.
 */
async function buildNovaSubscriptionPayload(
  initialCoach: CoachSelect,
): Promise<NovaSubscriptionPayload> {
  const { getNovaAccess } = await import("~/lib/server/nova/access");
  const {
    fetchNovaPrice,
    fetchNovaPriceForCoach,
    holdsDcmMembership,
    readNovaSubscription,
    rescueWalledNovaCoach,
  } = await import("~/lib/server/nova-billing");
  let coach = initialCoach;
  let access = await getNovaAccess(coach);

  // Opening Nova is the moment a coach who paid outside our checkout (a
  // dashboard payment link) discovers they're locked out — so it's also the
  // moment to rescue them. `rescueWalledNovaCoach` owns the condition (Stripe
  // gives no access, free access from staff aside) and returns null when there
  // was nothing to do; access is only re-derived when the claim actually
  // wrote something.
  const rescued = await rescueWalledNovaCoach(coach, access);
  if (rescued) {
    coach = rescued;
    access = await getNovaAccess(coach);
  }
  // The price is only rendered by the paywall (none) and the trial banner
  // (trialing) — skip the Stripe call on the hot subscribed path.
  //
  // A trialing coach is shown what THEY will be charged, read from their own
  // subscription: someone on the yearly term must not be told "$100.00/month"
  // when Stripe will invoice $997/year. The paywall has no subscription yet,
  // so it shows the monthly offer price. A trialing coach whose subscription
  // cannot be read falls back to the offer price rather than showing nothing.
  const price =
    access.state === "trialing"
      ? ((await fetchNovaPriceForCoach(coach)) ?? (await fetchNovaPrice()))
      : access.state === "none"
        ? await fetchNovaPrice()
        : null;
  // Readiness score presence + count — the funnel hero's pre-score → scored
  // gate. One read answers both so `hasScore` and `scoreSnapshotCount` cannot
  // disagree (the state machine's shape-consistency guard). The latest score
  // rides along for display only.
  const { readReadinessProgressForCoach } =
    await import("~/lib/server/readiness/assessments");
  const { hasPublishedOffer } = await import("~/lib/server/offer-lifecycle");
  const [scoreProgress, publishedOffer] = await Promise.all([
    readReadinessProgressForCoach(coach.id),
    hasPublishedOffer(coach.id),
  ]);
  return {
    ...readNovaSubscription(coach),
    access: access.state,
    enforced: access.enforced,
    trialEligible: access.trialEligible,
    member: holdsDcmMembership(coach),
    price,
    hasScore: scoreProgress.hasScore,
    scoreSnapshotCount: scoreProgress.scoreCount,
    latestScore: scoreProgress.latest,
    hasPublishedOffer: publishedOffer,
  };
}

export const getNovaSubscription = createServerFn({ method: "GET" })
  .middleware([requireNovaCoachAuth])
  .handler(async ({ context }): Promise<NovaSubscriptionPayload> => {
    return buildNovaSubscriptionPayload(context.coach);
  });

/**
 * Re-fetches the coach's Nova subscription from Stripe and reconciles the DB
 * columns before returning the same payload `getNovaSubscription` returns —
 * the self-healing counterpart called after checkout/portal returns and
 * billing-page loads, so a missed webhook delivery corrects itself instead of
 * leaving the coach stuck on stale access state.
 */
export const resyncNovaSubscription = createServerFn({ method: "POST" })
  .validator(
    z
      .object({ checkoutSessionId: z.string().min(1).max(255).optional() })
      .optional(),
  )
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context, data }): Promise<NovaSubscriptionPayload> => {
    const { syncNovaSubscriptionFromStripe } =
      await import("~/lib/server/nova/subscription/sync");
    await syncNovaSubscriptionFromStripe(context.coach, {
      trigger: "checkout_return",
      throwOnError: true,
      checkoutSessionId: data?.checkoutSessionId,
    });

    // context.coach is stale after the sync (it was read before this request's
    // handler ran the reconciliation) — re-read so the payload reflects any
    // correction just written. requireClerkCoachAuth only ever resolves a
    // coach via clerkUserId, so it is guaranteed to be set here.
    if (!context.coach.clerkUserId) {
      throw new Error("Coach missing clerkUserId after Clerk-authed request");
    }
    const { getCoachByClerkUserId } = await import("~/lib/server/coach");
    const freshCoach = await getCoachByClerkUserId(context.coach.clerkUserId);
    return buildNovaSubscriptionPayload(freshCoach ?? context.coach);
  });

export const createNovaCheckoutSession = createServerFn({ method: "POST" })
  .validator(novaCheckoutInputSchema)
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context, data }): Promise<{ url: string }> => {
    const { createNovaCheckoutUrl } = await import("~/lib/server/nova-billing");
    const requestUrl = getRequestUrl();
    const appUrl = getAppUrl({ url: requestUrl.href } as Request);
    const url = await createNovaCheckoutUrl({
      coach: context.coach,
      appUrl,
      returnTo: data?.returnTo,
      term: data?.term ?? "monthly",
      quotedPriceId: data?.quotedPriceId,
    });
    return { url };
  });

/**
 * Checkout for a suite plan. The lookup key comes from the client, so it is
 * validated against the closed suite set rather than passed through — a coach
 * must not be able to name an arbitrary Stripe price, and Nova is excluded so
 * its trial-bearing checkout stays the only way to subscribe to Nova.
 */
export const createSuitePlanCheckoutSession = createServerFn({ method: "POST" })
  .validator(
    z.object({
      // `z.custom` rather than `z.enum`: the accepted set is derived from the
      // catalogue, so it is an array rather than a literal tuple. The guard is
      // the same closed set either way, and it keeps the literal output type.
      lookupKey: z.custom<SuitePlanLookupKey>(isSuitePlanLookupKey, {
        message: "Unknown plan",
      }),
      /** The Stripe price the UI displayed. Checkout refuses if the catalogue
       *  has since moved the lookup key onto a different price. */
      quotedPriceId: z.string().max(255).optional(),
      returnTo: z.string().max(RETURN_PATH_MAX_LENGTH).optional(),
    }),
  )
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context, data }): Promise<{ url: string }> => {
    const { createSuitePlanCheckoutUrl } =
      await import("~/lib/server/nova-billing");
    const requestUrl = getRequestUrl();
    const appUrl = getAppUrl({ url: requestUrl.href } as Request);
    const url = await createSuitePlanCheckoutUrl({
      coach: context.coach,
      lookupKey: data.lookupKey,
      quotedPriceId: data.quotedPriceId,
      appUrl,
      returnTo: data.returnTo,
    });
    return { url };
  });

/**
 * Checkout for NOVA Pro+, for an Accelerator graduate with no NOVA plan. The
 * checkout reads her standing from her row and refuses anyone who has not
 * graduated, and everyone while `accelerator_pro_plus` is off for her.
 */
export const createProPlusCheckoutSession = createServerFn({ method: "POST" })
  .validator(
    z.object({
      term: z.enum(BILLING_TERMS),
      /** The Stripe price the row displayed. Checkout refuses if the catalogue
       *  has since moved the lookup key onto a different price. */
      quotedPriceId: z.string().max(255).optional(),
      returnTo: z.string().max(RETURN_PATH_MAX_LENGTH).optional(),
    }),
  )
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context, data }): Promise<{ url: string }> => {
    const { createProPlusCheckoutUrl } =
      await import("~/lib/server/nova-billing");
    const requestUrl = getRequestUrl();
    const appUrl = getAppUrl({ url: requestUrl.href } as Request);
    const url = await createProPlusCheckoutUrl({
      coach: context.coach,
      term: data.term,
      quotedPriceId: data.quotedPriceId,
      appUrl,
      returnTo: data.returnTo,
    });
    return { url };
  });

/**
 * The graduate Pro+ link (`/pro-plus`): where to send a signed-in visitor.
 * Any signed-in user may ask; someone without a coach row is told the link is
 * not for her rather than refused with an error.
 */
export const openProPlusLink = createServerFn({ method: "POST" })
  .validator(z.object({ term: z.enum(BILLING_TERMS).optional() }))
  .middleware([authMiddleware])
  .handler(async ({ context, data }): Promise<ProPlusLinkOutcome> => {
    const { clerkUserId, email, id: userId } = context.user;
    const { findAndLinkCoachForClerkUser } =
      await import("~/lib/server/coach-clerk-link");
    const { resolveProPlusLink } =
      await import("~/lib/server/accelerator/pro-plus");
    // Links her coach row by email on her first visit, as the layout's coach
    // status read does; this loader runs alongside it, not after it.
    const coach = email
      ? await findAndLinkCoachForClerkUser(clerkUserId, email, userId)
      : null;
    const appUrl = getAppUrl({ url: getRequestUrl().href } as Request);
    return resolveProPlusLink({ coach, term: data.term, appUrl });
  });

export const createNovaPortalSession = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context }): Promise<{ url: string }> => {
    const { createNovaPortalUrl } = await import("~/lib/server/nova-billing");
    const requestUrl = getRequestUrl();
    const appUrl = getAppUrl({ url: requestUrl.href } as Request);
    const url = await createNovaPortalUrl({
      coach: context.coach,
      appUrl,
    });
    return { url };
  });

/**
 * Best-effort post-mutation re-sync: pulls the just-updated subscription back
 * from Stripe and reconciles the DB columns before the caller returns, so the
 * client's immediate refetch reads fresh state instead of racing the
 * `customer.subscription.updated` webhook (which can lag seconds-to-minutes).
 * Sync failures are swallowed — the Stripe mutation already succeeded, and
 * the webhook remains the authoritative fallback sync path.
 */
async function bestEffortResyncAfterMutation(
  coach: CoachSelect,
): Promise<void> {
  try {
    const { syncNovaSubscriptionFromStripe } =
      await import("~/lib/server/nova/subscription/sync");
    await syncNovaSubscriptionFromStripe(coach, { trigger: "mutation" });
  } catch (error) {
    console.error(
      "[nova_billing] Post-mutation re-sync failed (webhook remains authoritative):",
      error instanceof Error ? error.message : String(error),
    );
  }
}

/**
 * In-app cancel of a trial or a paid plan, with the reason she gave first:
 * sets `cancel_at_period_end` on the authenticated coach's own Nova
 * subscription (never a client-supplied id), re-reads it so her columns and
 * `trial_cancelled` or `subscription_cancelled` are recorded before this
 * returns, then stores the reason (`cancelNovaWithReason`).
 */
export const cancelNovaSubscriptionFn = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(novaCancellationSchema)
  .handler(async ({ context, data }): Promise<{ success: true }> => {
    const { cancelNovaWithReason } =
      await import("~/lib/server/nova/cancellation-feedback");
    await cancelNovaWithReason(context.coach, data);
    return { success: true };
  });

/** What the paid cancel flow needs beyond her subscription state. */
export type NovaCancelOptions = {
  /** Her plan includes the Marketing Suite, so cancelling ends her editing
   *  rights there too. */
  includesSuite: boolean;
  /** The "too expensive" offer, or null when there is no plan below hers that
   *  the app would move her to now. */
  downgrade: NovaDowngradeOffer | null;
};

/**
 * The paid cancel flow's facts, including the downgrade offer that rides on
 * in-app plan changes.
 */
export const getNovaCancelOptionsFn = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context }): Promise<NovaCancelOptions> => {
    const plan = context.coach.novaPlanKey;
    const { readDowngradeOffer } =
      await import("~/lib/server/nova-plan-change");
    return {
      includesSuite: plan !== null && planGrantsMarketingSuite(plan),
      downgrade: await readDowngradeOffer(context.coach),
    };
  });

/**
 * Undo for `cancelNovaSubscriptionFn` — "Keep my trial" and "Keep NOVA" —
 * clears `cancel_at_period_end` on the authenticated coach's own Nova
 * subscription. Same auth derivation, same webhook-driven sync/spine path
 * (`trial_cancel_reversed` or `subscription_cancel_reversed`) plus the same
 * best-effort re-sync before returning.
 */
export const undoNovaCancelFn = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context }): Promise<{ success: true }> => {
    const { setNovaSubscriptionCancelAtPeriodEnd } =
      await import("~/lib/server/nova-billing");
    await setNovaSubscriptionCancelAtPeriodEnd(context.coach, false);
    await bestEffortResyncAfterMutation(context.coach);
    return { success: true };
  });

export const getNovaCheckoutOffer = createServerFn({ method: "GET" })
  .validator(
    z.object({ term: z.enum(BILLING_TERMS).default("monthly") }).optional(),
  )
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context, data }) => {
    const { readNovaCheckoutOffer } = await import("~/lib/server/nova-billing");
    return readNovaCheckoutOffer(context.coach, data?.term ?? "monthly");
  });

export type { NovaCheckoutOffer } from "~/lib/server/nova-billing";

const planChangeRequestSchema = z.object({
  mode: z.enum(PLAN_CHANGE_MODES),
  term: z.enum(BILLING_TERMS),
  promotionCode: z.string().trim().max(100).nullish(),
});

/**
 * What an in-app plan change would charge today and when the next charge
 * falls, from Stripe's invoice preview. The subscription is the authenticated
 * coach's own; the request names only the change.
 */
export const previewNovaPlanChangeFn = createServerFn({ method: "GET" })
  .validator(planChangeRequestSchema)
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context, data }): Promise<NovaPlanChangePreview> => {
    const { previewNovaPlanChange } =
      await import("~/lib/server/nova-plan-change");
    return previewNovaPlanChange(context.coach, data);
  });

/**
 * Makes a previewed plan change. The quote fields come back from the preview
 * so the server can refuse a change whose price or amount has moved; the
 * price itself is always resolved again from the catalogue.
 */
export const confirmNovaPlanChangeFn = createServerFn({ method: "POST" })
  .validator(
    planChangeRequestSchema.extend({
      prorationDate: z.number().int().positive(),
      quotedPriceId: z.string().min(1).max(255),
      quotedAmountDueCents: z.number().int().nonnegative(),
      quotedCreditCents: z.number().int().nonnegative(),
      quotedCurrency: z.string().length(3),
    }),
  )
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context, data }): Promise<NovaPlanChangeResult> => {
    const { confirmNovaPlanChange } =
      await import("~/lib/server/nova-plan-change");
    const result = await confirmNovaPlanChange(context.coach, data);
    if (result.kind === "changed")
      await bestEffortResyncAfterMutation(context.coach);
    return result;
  });
